import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { PermissionsService } from '@app-common/services/permissions/permissions.service';
import { matchRoutePermission } from './route-permissions';

/**
 * Route guard factory: allows activation only if the current user holds at least one of
 * the given permission codes (admins always pass). Redirects home otherwise.
 *
 *   { path: 'authorization', canActivate: [hasPermissionGuard('rbac.access')], ... }
 */
export function hasPermissionGuard(...codes: string[]): CanActivateFn
{
    return () => {
        const permissions = inject(PermissionsService);
        const router = inject(Router);

        return permissions.ensureLoaded().pipe(
            map(() => {
                if (codes.length === 0 || permissions.hasAny(codes)) {
                    return true;
                }
                return router.createUrlTree(['/']);
            })
        );
    };
}

/**
 * Shell-level guard: reads the target route's required permission from the central
 * route->permission map and blocks navigation (redirect home) if the user lacks it.
 * Unmapped routes are allowed. Add once to the app shell's canActivateChild.
 */
export const permissionChildGuard: CanActivateChildFn = (_childRoute, state) => {
    const permissions = inject(PermissionsService);
    const router = inject(Router);

    const required = matchRoutePermission(state.url);
    if (!required) {
        return true;
    }

    return permissions.ensureLoaded().pipe(
        map(() => permissions.has(required) ? true : router.createUrlTree(['/']))
    );
};
